import {
  extend,
  bool as boolType,
  params,
  shape,
  string as stringType,
  number as numberType,
  arrayOf,
} from '@thd-nucleus/data-sources';
import { Buybox } from '@thd-olt-component-react/buybox';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';

const ProductPodAndMediaGallery = extend(
  {},
  ProductPod.dataModel.product,
  MediaGalleryDesktop.dataModel.product,
  {
    identifiers: shape({
      itemId: stringType(),
      isSuperSku: boolType(),
      parentId: stringType(),
      canonicalUrl: stringType(),
      brandName: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      productType: stringType(),
    }),
    details: shape({
      description: stringType(),
    }),
    info: shape({
      classNumber: stringType(),
      subClassNumber: stringType(),
      productDepartment: stringType(),
      quantityLimit: stringType(),
      hasServiceAddOns: stringType(),
    }),
    availabilityType: shape({
      discontinued: stringType(),
      status: stringType(),
      type: stringType(),
      buyable: stringType(),
    }),
    pricing: params({
      storeId: stringType(),
      isBrandPricingPolicyCompliant: boolType()
    }).shape({
      alternatePriceDisplay: boolType(),
      alternate: shape({
        bulk: shape({
          pricePerUnit: numberType({ float: true }),
          thresholdQuantity: numberType(),
          value: numberType({ float: true })
        }),
        unit: {
          caseUnitOfMeasure: stringType(),
          unitsOriginalPrice: numberType({ float: true }),
          unitsPerCase: numberType({ float: true }),
          value: numberType({ float: true })
        }
      }),
      original: numberType({ float: true }),
      mapAboveOriginalPrice: boolType(),
      mapDetail: shape({
        percentageOff: numberType({ float: true }),
        dollarOff: numberType({ float: true }),
        mapPolicy: stringType(),
        mapOriginalPriceViolation: boolType(),
        mapSpecialPriceViolation: boolType(),
      }),
      message: stringType(),
      preferredPriceFlag: boolType(),
      promotion: shape({
        type: stringType(),
        description: shape({
          shortDesc: stringType(),
          longDesc: stringType()
        }),
        dollarOff: numberType({ float: true }),
        percentageOff: numberType({ float: true }),
        promotionTag: stringType(),
        savingsCenter: stringType(),
        savingsCenterPromos: stringType(),
        specialBuySavings: stringType(),
        specialBuyDollarOff: numberType({ float: true }),
        specialBuyPercentageOff: numberType({ float: true })
      }),
      specialBuy: numberType({ float: true }),
      unitOfMeasure: stringType(),
      value: numberType({ float: true })
    }),
    fulfillment: params({ storeId: stringType() }).shape({
      backordered: boolType(),
      onlineStoreStatus: stringType(),
      fulfillmentOptions: arrayOf(
        shape({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOf(
            shape({
              type: stringType(),
              deliveryTimeline: stringType(),
              locations: arrayOf(
                shape({
                  isAnchor: boolType(),
                  inventory: shape({
                    isLimitedQuantity: boolType(),
                    isOutOfStock: boolType(),
                  }),
                })
              ),
            })
          ),
        })
      ),
    }),
  }
);

export const productConfiguratorDataModel = extend(
  {
    product: params({
      itemId: stringType().isRequired()
    }).shape({
      itemId: stringType(),
      dataSource: stringType(),
      identifiers: shape({
        productLabel: stringType(),
      }),
    }),
  },
  {
    clientOnlyProduct: params({
      itemId: stringType().isRequired()
    }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      info: shape({
        gccExperienceOmsId: stringType(),
        recommendationFlags: shape({
          visualNavigation: boolType(),
          pipCollections: boolType(),
          packages: boolType(),
          ACC: boolType(),
          collections: boolType(),
          frequentlyBoughtTogether: boolType(),
          bundles: boolType(),
          batItems: boolType(),
        }),
        dotComColorEligible: boolType(),
        globalCustomConfigurator: shape({
          customPosition: stringType(),
        }),
      }),
      identifiers: shape({
        roomVOEnabled: boolType(),
        productType: stringType(),
        skuClassification: stringType(),
      }),
      availabilityType: shape({
        discontinued: boolType(),
        type: stringType(),
      }),
      fulfillment: params({ storeId: stringType() }).shape({
        backordered: boolType(),
        fulfillmentOptions: arrayOf(
          shape({
            type: stringType(),
            fulfillable: boolType(),
            services: arrayOf(
              shape({
                type: stringType(),
                locations: arrayOf(
                  shape({
                    isAnchor: boolType(),
                    inventory: shape({
                      isLimitedQuantity: boolType(),
                      isOutOfStock: boolType(),
                    }),
                  })
                ),
              })
            ),
          })
        ),
      }),
    }),
  },
  {
    clientOnlyProduct: params({ itemId: stringType().isRequired }).shape({
      seoDescription: stringType(),
    }),
  },
  {
    products: params({
      dataSource: stringType(),
      itemIds: arrayOf(stringType().isRequired()).isRequired()
    }).arrayOf(ProductPodAndMediaGallery),
  },
  Buybox
);
